import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import PropTypes from 'prop-types';
import { BarChart, Label, Tooltip, Legend, CartesianGrid, XAxis, YAxis, Bar, ResponsiveContainer } from 'recharts';

import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { emptyArray } from 'ui-components/default-props/empty';
import { getInterval } from '../../helper';
import Colors from '../utilities/colors';
import { MAIN_GRAPH_HEIGHT, Y_AXIS_TICK_LENGTH } from '../utilities/constants';
import CustomTooltip from '../custom-tooltip';
import CustomTooltipNewOrders from '../custom-tooltip/custom-tooltip-new-orders-per-day';
import style from './style.module.less';
import { useUserDateFormat } from '../../../../../hooks/use-datetime-formats';

import messages from './messages';
import analyticsStyle from '../../../analytics-style.module.less';

const NewOrdersEachDayGraph = ({ data = emptyArray }) => {
  const intl = useIntl();
  const formattedData = useMemo(() => {
    return data.map((datum) => ({
      ...datum,
      unshippedCount: datum.newCount - datum.shippedCount,
    }));
  }, [data]);
  const { formatUserDate } = useUserDateFormat();

  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const messageKey = useMemo(
    () => ({
      shippedCount: intl.formatMessage(messages.shipped),
      unshippedCount: intl.formatMessage(messages.unshipped),
    }),
    [intl],
  );

  const interval = useMemo(() => getInterval(formattedData), [formattedData]);

  const formatXTicks = useCallback(
    (date) => {
      return formatUserDate(date, false);
    },
    [formatUserDate],
  );

  const formatLegend = useCallback(
    (value) => {
      return <span className={style['legend-label']}>{messageKey[value]}</span>;
    },
    [messageKey],
  );

  return (
    <>
      {isShipStationAnalyticsFeatureEnabled && (
        <Text className={analyticsStyle['analytics-section-title']} textType={TextType.H4} value={messages.orderChartTitle} />
      )}
      <ResponsiveContainer height={MAIN_GRAPH_HEIGHT} width='100%'>
        <BarChart
          data={formattedData}
          style={{
            fontSize: 12,
          }}
        >
          <CartesianGrid stroke={Colors.GREY_HEX_VALUE} />
          <XAxis dataKey='interval' interval={interval} tickFormatter={formatXTicks} tickLine={false} />
          <YAxis tickLine={false} tickSize={Y_AXIS_TICK_LENGTH}>
            <Label
              angle={-90}
              className={classnames(style['yAxis-label'], {
                [analyticsStyle['analytics-yAxis-label']]: isShipStationAnalyticsFeatureEnabled,
              })}
              position='insideLeft'
              value={intl.formatMessage(messages.yAxisLabel)}
            />
          </YAxis>
          <Legend
            formatter={formatLegend}
            style={{ width: 80 }}
            width='100%'
            wrapperStyle={{
              display: 'flex',
              justifyContent: 'center',
              marginLeft: '25px',
            }}
          />
          <Tooltip
            content={
              <CustomTooltip>
                <CustomTooltipNewOrders />
              </CustomTooltip>
            }
          />
          <Bar dataKey='shippedCount' fill={Colors.BAR_COLOR_PURPLE} stackId='a' />
          <Bar dataKey='unshippedCount' fill={Colors.BAR_COLOR_BLUE} stackId='a' />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

NewOrdersEachDayGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      interval: PropTypes.string,
      shippedCount: PropTypes.number,
      amount: PropTypes.number,
      itemCount: PropTypes.number,
    }),
  ),
};

export default NewOrdersEachDayGraph;
